/**
 * Vehicle Hardware Abstraction Layer (VHAL) Types
 *
 * Full property IDs sourced from AOSP VehiclePropertyIds.java (Android 15).
 * Property IDs encode: [group 4bits][area 4bits][type 8bits][base 16bits]
 *
 * @see https://source.android.com/docs/automotive/vhal
 * @see https://developer.android.com/reference/android/car/VehiclePropertyIds
 */

/**
 * VHAL Property Data Types
 */
export enum VhalPropertyType {
  STRING = 0x00100000,
  BOOLEAN = 0x00200000,
  INT32 = 0x00400000,
  INT32_VEC = 0x00410000,
  INT64 = 0x00500000,
  INT64_VEC = 0x00510000,
  FLOAT = 0x00600000,
  FLOAT_VEC = 0x00610000,
  BYTES = 0x00700000,
  MIXED = 0x00e00000,
}

/**
 * VHAL Property Access Mode
 */
export enum VhalPropertyAccess {
  NONE = 0x00,
  READ = 0x01,
  WRITE = 0x02,
  READ_WRITE = 0x03,
}

/**
 * VHAL Property Change Mode
 */
export enum VhalPropertyChangeMode {
  STATIC = 0x00,
  ON_CHANGE = 0x01,
  CONTINUOUS = 0x02,
}

/**
 * VHAL Property Group
 */
export enum VhalPropertyGroup {
  SYSTEM = 0x10000000,
  VENDOR = 0x20000000,
}

/**
 * VHAL Area Type
 */
export enum VhalAreaType {
  GLOBAL = 0x01000000,
  WINDOW = 0x03000000,
  MIRROR = 0x04000000,
  SEAT = 0x05000000,
  DOOR = 0x06000000,
  WHEEL = 0x07000000,
}

/**
 * VHAL Property Status
 */
export enum VhalPropertyStatus {
  AVAILABLE = 0,
  UNAVAILABLE = 1,
  ERROR = 2,
}

/**
 * Common VHAL area IDs for zoned properties.
 */
export const VhalAreaId = {
  GLOBAL: 0x0,
  // Seat areas
  ROW_1_LEFT: 0x1,
  ROW_1_CENTER: 0x2,
  ROW_1_RIGHT: 0x4,
  ROW_2_LEFT: 0x10,
  ROW_2_CENTER: 0x20,
  ROW_2_RIGHT: 0x40,
  // Door areas
  DOOR_ROW_1_LEFT: 0x1,
  DOOR_ROW_1_RIGHT: 0x4,
  DOOR_ROW_2_LEFT: 0x10,
  DOOR_ROW_2_RIGHT: 0x40,
  DOOR_REAR: 0x20000000,
  // Window areas
  FRONT_WINDSHIELD: 0x1,
  REAR_WINDSHIELD: 0x2,
  WINDOW_ROW_1_LEFT: 0x10,
  WINDOW_ROW_1_RIGHT: 0x40,
  WINDOW_ROW_2_LEFT: 0x100,
  WINDOW_ROW_2_RIGHT: 0x400,
  WINDOW_ROOF_TOP_1: 0x10000,
  // Mirror areas
  DRIVER_LEFT: 0x1,
  DRIVER_RIGHT: 0x2,
  DRIVER_CENTER: 0x4,
  DRIVER_LEFT_RIGHT: 0x3,
  // Wheel areas
  LEFT_FRONT: 0x1,
  RIGHT_FRONT: 0x2,
  LEFT_REAR: 0x4,
  RIGHT_REAR: 0x8,
} as const;

/**
 * Build a full VHAL property ID from its components.
 */
export function buildPropertyId(
  group: VhalPropertyGroup,
  area: VhalAreaType,
  type: VhalPropertyType,
  base: number,
): number {
  return group | area | type | base;
}

/**
 * Common VHAL Property IDs — corrected against Android 15 Cuttlefish emulator.
 *
 * Full ID = GROUP | AREA_TYPE | PROPERTY_TYPE | BASE_ID
 */
export enum VhalPropertyId {
  // ── Info ──────────────────────────────────────────────────────────
  INFO_VIN = 0x11100100,
  INFO_MAKE = 0x11100101,
  INFO_MODEL = 0x11100102,
  INFO_MODEL_YEAR = 0x11400103,
  INFO_FUEL_CAPACITY = 0x11600104,
  INFO_FUEL_TYPE = 0x11410105,
  INFO_EV_BATTERY_CAPACITY = 0x11600106,
  INFO_EV_CONNECTOR_TYPE = 0x11410107,
  INFO_FUEL_DOOR_LOCATION = 0x11400108,
  INFO_EV_PORT_LOCATION = 0x11400109,
  INFO_DRIVER_SEAT = 0x1540010a,
  INFO_EXTERIOR_DIMENSIONS = 0x1141010b,
  INFO_MULTI_EV_PORT_LOCATIONS = 0x1141010c,

  // ── Performance ──────────────────────────────────────────────────
  PERF_ODOMETER = 0x11600204,
  PERF_VEHICLE_SPEED = 0x11600207,
  PERF_VEHICLE_SPEED_DISPLAY = 0x11600208,
  PERF_STEERING_ANGLE = 0x11600209,
  PERF_REAR_STEERING_ANGLE = 0x11600210,

  // ── Engine ───────────────────────────────────────────────────────
  ENGINE_COOLANT_TEMP = 0x11600301,
  ENGINE_OIL_LEVEL = 0x11400303,
  ENGINE_OIL_TEMP = 0x11600304,
  ENGINE_RPM = 0x11600305,
  ENGINE_IDLE_AUTO_STOP_ENABLED = 0x11200320,

  // ── Fuel ─────────────────────────────────────────────────────────
  FUEL_LEVEL = 0x11600307,
  FUEL_DOOR_OPEN = 0x11200308,
  RANGE_REMAINING = 0x11600308,
  FUEL_LEVEL_LOW = 0x11200405,
  WHEEL_TICK = 0x11510306,

  // ── EV / Battery ─────────────────────────────────────────────────
  EV_BATTERY_LEVEL = 0x11600309,
  EV_CHARGE_PORT_OPEN = 0x1120030a,
  EV_CHARGE_PORT_CONNECTED = 0x1120030b,
  EV_BATTERY_INSTANTANEOUS_CHARGE_RATE = 0x1160030c,
  EV_CURRENT_BATTERY_CAPACITY = 0x1160030d,
  EV_BATTERY_AVERAGE_TEMPERATURE = 0x1160030e,
  IMPACT_DETECTED = 0x11400330,
  EV_CHARGE_CURRENT_DRAW_LIMIT = 0x11600f3f,
  EV_CHARGE_PERCENT_LIMIT = 0x11600f40,
  EV_CHARGE_STATE = 0x11400f41,
  EV_CHARGE_SWITCH = 0x11200f42,
  EV_CHARGE_TIME_REMAINING = 0x11400f43,
  EV_REGENERATIVE_BRAKING_STATE = 0x11400f44,
  EV_BRAKE_REGENERATION_LEVEL = 0x1140040c,
  EV_STOPPING_MODE = 0x1140040d,

  // ── Transmission ─────────────────────────────────────────────────
  GEAR_SELECTION = 0x11400400,
  CURRENT_GEAR = 0x11400401,
  PARKING_BRAKE_ON = 0x11200402,
  PARKING_BRAKE_AUTO_APPLY = 0x11200403,
  IGNITION_STATE = 0x11400409,
  TURN_SIGNAL_STATE = 0x11400408,
  ABS_ACTIVE = 0x1120040a,
  TRACTION_CONTROL_ACTIVE = 0x1120040b,
  ELECTRONIC_STABILITY_CONTROL_ENABLED = 0x1120040e,
  ELECTRONIC_STABILITY_CONTROL_STATE = 0x1140040f,

  // ── HVAC (SEAT-zoned unless noted) ──────────────────────────────
  HVAC_FAN_SPEED = 0x15400500,
  HVAC_FAN_DIRECTION = 0x15400501,
  HVAC_TEMPERATURE_CURRENT = 0x15600502,
  HVAC_TEMPERATURE_SET = 0x15600503,
  HVAC_DEFROSTER = 0x13200504, // WINDOW area
  HVAC_AC_ON = 0x15200505,
  HVAC_MAX_AC_ON = 0x15200506,
  HVAC_MAX_DEFROST_ON = 0x15200507, // SEAT area
  HVAC_RECIRC_ON = 0x15200508,
  HVAC_DUAL_ON = 0x15200509,
  HVAC_AUTO_ON = 0x1520050a,
  HVAC_SEAT_TEMPERATURE = 0x1540050b,
  HVAC_SIDE_MIRROR_HEAT = 0x1440050c, // MIRROR area
  HVAC_STEERING_WHEEL_HEAT = 0x1140050d,
  HVAC_TEMPERATURE_DISPLAY_UNITS = 0x1140050e,
  HVAC_ACTUAL_FAN_SPEED_RPM = 0x1540050f,
  HVAC_POWER_ON = 0x15200510,
  HVAC_FAN_DIRECTION_AVAILABLE = 0x15410511,
  HVAC_AUTO_RECIRC_ON = 0x15200512,
  HVAC_SEAT_VENTILATION = 0x15400513,
  HVAC_ELECTRIC_DEFROSTER_ON = 0x13200514, // WINDOW area
  HVAC_TEMPERATURE_VALUE_SUGGESTION = 0x11610515,

  // ── Display Units ────────────────────────────────────────────────
  DISTANCE_DISPLAY_UNITS = 0x11400600,
  FUEL_VOLUME_DISPLAY_UNITS = 0x11400601,
  TIRE_PRESSURE_DISPLAY_UNITS = 0x11400602,
  EV_BATTERY_DISPLAY_UNITS = 0x11400603,
  FUEL_CONSUMPTION_UNITS_DISTANCE_OVER_VOLUME = 0x11200604,
  VEHICLE_SPEED_DISPLAY_UNITS = 0x11400605,

  // ── Environment ──────────────────────────────────────────────────
  ENV_OUTSIDE_TEMPERATURE = 0x11600703,

  // ── System / Power ───────────────────────────────────────────────
  AP_POWER_STATE_REQ = 0x11410a00,
  AP_POWER_STATE_REPORT = 0x11410a01,
  AP_POWER_BOOTUP_REASON = 0x11400a02,
  DISPLAY_BRIGHTNESS = 0x11400a03,
  VALET_MODE_ENABLED = 0x11200a05,
  HEAD_UP_DISPLAY_ENABLED = 0x15200a06,
  HW_KEY_INPUT = 0x11410a10,
  HW_KEY_INPUT_V2 = 0x15e00a11,
  HW_ROTARY_INPUT = 0x11410a20,
  HW_CUSTOM_INPUT = 0x11410a30,

  // ── User HAL (CarUserService request/response) ──────────────────
  INITIAL_USER_INFO = 0x11e00f07,
  SWITCH_USER = 0x11e00f08,
  CREATE_USER = 0x11e00f09,
  REMOVE_USER = 0x11e00f0a,

  // ── Night Mode ───────────────────────────────────────────────────
  NIGHT_MODE = 0x11200407,

  // ── Doors ────────────────────────────────────────────────────────
  DOOR_POS = 0x16400b00,
  DOOR_MOVE = 0x16400b01,
  DOOR_LOCK = 0x16200b02,
  DOOR_CHILD_LOCK_ENABLED = 0x16200b03,

  // ── Mirrors ──────────────────────────────────────────────────────
  MIRROR_Z_POS = 0x14400b40,
  MIRROR_Z_MOVE = 0x14400b41,
  MIRROR_Y_POS = 0x14400b42,
  MIRROR_Y_MOVE = 0x14400b43,
  MIRROR_LOCK = 0x11200b44,
  MIRROR_FOLD = 0x11200b45,
  MIRROR_AUTO_FOLD_ENABLED = 0x14200b46,
  MIRROR_AUTO_TILT_ENABLED = 0x14200b47,

  // ── Seats ────────────────────────────────────────────────────────
  SEAT_MEMORY_SELECT = 0x15400b80,
  SEAT_MEMORY_SET = 0x15400b81,
  SEAT_BELT_BUCKLED = 0x15200b82,
  SEAT_BELT_HEIGHT_POS = 0x15400b83,
  SEAT_BELT_HEIGHT_MOVE = 0x15400b84,
  SEAT_FORE_AFT_POS = 0x15400b85,
  SEAT_FORE_AFT_MOVE = 0x15400b86,
  SEAT_BACKREST_ANGLE_1_POS = 0x15400b87,
  SEAT_BACKREST_ANGLE_1_MOVE = 0x15400b88,
  SEAT_BACKREST_ANGLE_2_POS = 0x15400b89,
  SEAT_BACKREST_ANGLE_2_MOVE = 0x15400b8a,
  SEAT_HEIGHT_POS = 0x15400b8b,
  SEAT_HEIGHT_MOVE = 0x15400b8c,
  SEAT_DEPTH_POS = 0x15400b8d,
  SEAT_DEPTH_MOVE = 0x15400b8e,
  SEAT_TILT_POS = 0x15400b8f,
  SEAT_TILT_MOVE = 0x15400b90,
  SEAT_LUMBAR_FORE_AFT_POS = 0x15400b91,
  SEAT_LUMBAR_FORE_AFT_MOVE = 0x15400b92,
  SEAT_LUMBAR_SIDE_SUPPORT_POS = 0x15400b93,
  SEAT_LUMBAR_SIDE_SUPPORT_MOVE = 0x15400b94,
  SEAT_HEADREST_HEIGHT_MOVE = 0x15400b96,
  SEAT_HEADREST_ANGLE_POS = 0x15400b97,
  SEAT_HEADREST_ANGLE_MOVE = 0x15400b98,
  SEAT_HEADREST_FORE_AFT_POS = 0x15400b99,
  SEAT_HEADREST_FORE_AFT_MOVE = 0x15400b9a,
  SEAT_FOOTWELL_LIGHTS_STATE = 0x15400b9b,
  SEAT_FOOTWELL_LIGHTS_SWITCH = 0x15400b9c,
  SEAT_EASY_ACCESS_ENABLED = 0x15200b9d,
  SEAT_AIRBAG_ENABLED = 0x15200b9e,
  SEAT_CUSHION_SIDE_SUPPORT_POS = 0x15400b9f,
  SEAT_CUSHION_SIDE_SUPPORT_MOVE = 0x15400ba0,
  SEAT_LUMBAR_VERTICAL_POS = 0x15400ba1,
  SEAT_LUMBAR_VERTICAL_MOVE = 0x15400ba2,
  SEAT_WALK_IN_POS = 0x15400ba3,
  SEAT_HEADREST_HEIGHT_POS_V2 = 0x15400ba4,
  SEAT_AIRBAGS_DEPLOYED = 0x15400ba5,
  SEAT_BELT_PRETENSIONER_DEPLOYED = 0x15200ba6,
  SEAT_OCCUPANCY = 0x15400bb0,

  // ── Windows ──────────────────────────────────────────────────────
  WINDOW_POS = 0x13400bc0,
  WINDOW_MOVE = 0x13400bc1,
  WINDOW_LOCK = 0x13200bc4,
  WINDSHIELD_WIPERS_PERIOD = 0x13400bc5,
  WINDSHIELD_WIPERS_STATE = 0x13400bc6,
  WINDSHIELD_WIPERS_SWITCH = 0x13400bc7,

  // ── Steering Wheel ───────────────────────────────────────────────
  STEERING_WHEEL_DEPTH_POS = 0x11400be0,
  STEERING_WHEEL_DEPTH_MOVE = 0x11400be1,
  STEERING_WHEEL_HEIGHT_POS = 0x11400be2,
  STEERING_WHEEL_HEIGHT_MOVE = 0x11400be3,
  STEERING_WHEEL_THEFT_LOCK_ENABLED = 0x11200be4,
  STEERING_WHEEL_LOCKED = 0x11200be5,
  STEERING_WHEEL_EASY_ACCESS_ENABLED = 0x11200be6,

  // ── Glove Box ────────────────────────────────────────────────────
  GLOVE_BOX_DOOR_POS = 0x15400bf0,
  GLOVE_BOX_LOCKED = 0x15200bf1,

  // ── Lights ───────────────────────────────────────────────────────
  HEADLIGHTS_STATE = 0x11400e00,
  HIGH_BEAM_LIGHTS_STATE = 0x11400e01,
  FOG_LIGHTS_STATE = 0x11400e02,
  HAZARD_LIGHTS_STATE = 0x11400e03,
  HEADLIGHTS_SWITCH = 0x11400e10,
  HIGH_BEAM_LIGHTS_SWITCH = 0x11400e11,
  FOG_LIGHTS_SWITCH = 0x11400e12,
  HAZARD_LIGHTS_SWITCH = 0x11400e13,
  CABIN_LIGHTS_STATE = 0x11400f01,
  CABIN_LIGHTS_SWITCH = 0x11400f02,
  READING_LIGHTS_STATE = 0x15400f03,
  READING_LIGHTS_SWITCH = 0x15400f04,
  STEERING_WHEEL_LIGHTS_STATE = 0x11400f0c,
  STEERING_WHEEL_LIGHTS_SWITCH = 0x11400f0d,
  FRONT_FOG_LIGHTS_STATE = 0x11400f3b,
  FRONT_FOG_LIGHTS_SWITCH = 0x11400f3c,
  REAR_FOG_LIGHTS_STATE = 0x11400f3d,
  REAR_FOG_LIGHTS_SWITCH = 0x11400f3e,

  // ── Cruise Control / ADAS ────────────────────────────────────────
  CRUISE_CONTROL_ENABLED = 0x1120100f,
  CRUISE_CONTROL_TYPE = 0x11401010,
  CRUISE_CONTROL_STATE = 0x11401011,
  CRUISE_CONTROL_COMMAND = 0x11401012,
  CRUISE_CONTROL_TARGET_SPEED = 0x11601013,
  ADAPTIVE_CRUISE_CONTROL_TARGET_TIME_GAP = 0x11401014,
  ADAPTIVE_CRUISE_CONTROL_LEAD_VEHICLE_MEASURED_DISTANCE = 0x11401015,

  AUTOMATIC_EMERGENCY_BRAKING_ENABLED = 0x11201000,
  AUTOMATIC_EMERGENCY_BRAKING_STATE = 0x11401001,
  FORWARD_COLLISION_WARNING_ENABLED = 0x11201002,
  FORWARD_COLLISION_WARNING_STATE = 0x11401003,
  BLIND_SPOT_WARNING_ENABLED = 0x11201004,
  BLIND_SPOT_WARNING_STATE = 0x14401005, // MIRROR area
  LANE_DEPARTURE_WARNING_ENABLED = 0x11201006,
  LANE_DEPARTURE_WARNING_STATE = 0x11401007,
  LANE_KEEP_ASSIST_ENABLED = 0x11201008,
  LANE_KEEP_ASSIST_STATE = 0x11401009,
  LANE_CENTERING_ASSIST_ENABLED = 0x1120100a,
  LANE_CENTERING_ASSIST_COMMAND = 0x1140100b,
  LANE_CENTERING_ASSIST_STATE = 0x1140100c,
  EMERGENCY_LANE_KEEP_ASSIST_ENABLED = 0x1120100d,
  EMERGENCY_LANE_KEEP_ASSIST_STATE = 0x1140100e,
  HANDS_ON_DETECTION_ENABLED = 0x11201016,
  HANDS_ON_DETECTION_DRIVER_STATE = 0x11401017,
  HANDS_ON_DETECTION_WARNING = 0x11401018,
  DRIVER_DROWSINESS_ATTENTION_SYSTEM_ENABLED = 0x11201019,
  DRIVER_DROWSINESS_ATTENTION_STATE = 0x1140101a,
  DRIVER_DROWSINESS_ATTENTION_WARNING_ENABLED = 0x1120101b,
  DRIVER_DROWSINESS_ATTENTION_WARNING = 0x1140101c,
  DRIVER_DISTRACTION_SYSTEM_ENABLED = 0x1120101d,
  DRIVER_DISTRACTION_STATE = 0x1140101e,
  DRIVER_DISTRACTION_WARNING_ENABLED = 0x1120101f,
  DRIVER_DISTRACTION_WARNING = 0x11401020,
  LOW_SPEED_COLLISION_WARNING_ENABLED = 0x11201021,
  LOW_SPEED_COLLISION_WARNING_STATE = 0x11401022,
  CROSS_TRAFFIC_MONITORING_ENABLED = 0x11201023,
  CROSS_TRAFFIC_MONITORING_WARNING_STATE = 0x11401024,
  LOW_SPEED_AUTOMATIC_EMERGENCY_BRAKING_ENABLED = 0x11201025,
  LOW_SPEED_AUTOMATIC_EMERGENCY_BRAKING_STATE = 0x11401026,

  // ── Tires / Wheels ───────────────────────────────────────────────
  TIRE_PRESSURE = 0x17600309,
  CRITICALLY_LOW_TIRE_PRESSURE = 0x1760030a,

  // ── Misc Global ──────────────────────────────────────────────────
  VEHICLE_CURB_WEIGHT = 0x11400f46,
  VEHICLE_IN_USE = 0x11200f4a,
  VEHICLE_DRIVING_AUTOMATION_CURRENT_LEVEL = 0x11400f4c,
  TRAILER_PRESENT = 0x11400f45,
  GENERAL_SAFETY_REGULATION_COMPLIANCE_REQUIREMENT = 0x11400f47,
  ANDROID_EPOCH_TIME = 0x11500606,

  // ── Vendor ───────────────────────────────────────────────────────
  VENDOR_START = 0x20000000,
}

/**
 * Property metadata (full configuration from VHAL)
 */
export interface VhalPropertyConfig {
  propertyId: number;
  access: VhalPropertyAccess;
  changeMode: VhalPropertyChangeMode;
  areaConfigs?: VhalAreaConfig[];
  configArray?: number[];
  configString?: string;
  minSampleRate?: number;
  maxSampleRate?: number;
  propertyType?: VhalPropertyType;
  areaType?: VhalAreaType;
  propertyGroup?: VhalPropertyGroup;
  supportVariableUpdateRate?: boolean;
  supportedEnumValues?: number[];
  name?: string;
}

/**
 * Area configuration for properties that support zones
 */
export interface VhalAreaConfig {
  areaId: number;
  minInt32Value?: number;
  maxInt32Value?: number;
  minInt64Value?: bigint;
  maxInt64Value?: bigint;
  minFloatValue?: number;
  maxFloatValue?: number;
  supportedEnumValues?: number[];
}

/**
 * Property value
 */
export interface VhalPropertyValue {
  propertyId: number;
  areaId?: number;
  status?: VhalPropertyStatus;
  timestamp?: number;
  int32Values?: number[];
  int64Values?: bigint[];
  floatValues?: number[];
  stringValue?: string;
  byteValues?: Uint8Array;
}

/**
 * Human-readable property information
 */
export interface VhalPropertyInfo {
  id: number;
  name: string;
  type: VhalPropertyType;
  description: string;
  units?: string;
  category: string;
  readOnly?: boolean;
  minValue?: number | bigint;
  maxValue?: number | bigint;
  enumValues?: Record<number, string>;
}

/**
 * Property categories for UI organization
 */
export const VHAL_PROPERTY_CATEGORIES = [
  "Performance",
  "Engine",
  "Fuel",
  "Battery",
  "Transmission",
  "HVAC",
  "Environment",
  "Info",
  "Lights",
  "Doors",
  "Windows",
  "Seats",
  "Mirrors",
  "Steering",
  "Tires",
  "ADAS",
  "Cruise Control",
  "Display",
  "Safety",
  "Wipers",
  "Vendor",
  "Unknown",
] as const;

export type VhalPropertyCategory = (typeof VHAL_PROPERTY_CATEGORIES)[number];

export function getPropertyType(propertyId: number): VhalPropertyType {
  return (propertyId & 0x00ff0000) as VhalPropertyType;
}

export function getAreaType(propertyId: number): VhalAreaType {
  return (propertyId & 0x0f000000) as VhalAreaType;
}

export function getPropertyGroup(propertyId: number): VhalPropertyGroup {
  return (propertyId & 0xf0000000) as VhalPropertyGroup;
}

export function getBasePropertyId(propertyId: number): number {
  return propertyId & 0x0000ffff;
}

export function isVendorProperty(propertyId: number): boolean {
  return (propertyId & 0xf0000000) === VhalPropertyGroup.VENDOR;
}

export function isZonedProperty(propertyId: number): boolean {
  const areaType = getAreaType(propertyId);
  return areaType !== VhalAreaType.GLOBAL;
}

export function getAreaTypeName(areaType: VhalAreaType): string {
  switch (areaType) {
    case VhalAreaType.GLOBAL: return "Global";
    case VhalAreaType.WINDOW: return "Window";
    case VhalAreaType.MIRROR: return "Mirror";
    case VhalAreaType.SEAT: return "Seat";
    case VhalAreaType.DOOR: return "Door";
    case VhalAreaType.WHEEL: return "Wheel";
    default: return "Unknown";
  }
}

export function getAccessModeName(access: VhalPropertyAccess): string {
  switch (access) {
    case VhalPropertyAccess.NONE: return "None";
    case VhalPropertyAccess.READ: return "Read";
    case VhalPropertyAccess.WRITE: return "Write";
    case VhalPropertyAccess.READ_WRITE: return "Read/Write";
    default: return "Unknown";
  }
}

export function getChangeModeName(changeMode: VhalPropertyChangeMode): string {
  switch (changeMode) {
    case VhalPropertyChangeMode.STATIC: return "Static";
    case VhalPropertyChangeMode.ON_CHANGE: return "On Change";
    case VhalPropertyChangeMode.CONTINUOUS: return "Continuous";
    default: return "Unknown";
  }
}

export function getPropertyTypeName(type: VhalPropertyType): string {
  switch (type) {
    case VhalPropertyType.STRING: return "STRING";
    case VhalPropertyType.BOOLEAN: return "BOOLEAN";
    case VhalPropertyType.INT32: return "INT32";
    case VhalPropertyType.INT32_VEC: return "INT32_VEC";
    case VhalPropertyType.INT64: return "INT64";
    case VhalPropertyType.INT64_VEC: return "INT64_VEC";
    case VhalPropertyType.FLOAT: return "FLOAT";
    case VhalPropertyType.FLOAT_VEC: return "FLOAT_VEC";
    case VhalPropertyType.BYTES: return "BYTES";
    case VhalPropertyType.MIXED: return "MIXED";
    default: return "UNKNOWN";
  }
}

const LIGHT_ENUMS = { 0: "OFF", 1: "ON", 2: "DAYTIME_RUNNING" } as const;
const LIGHT_SWITCH_ENUMS = { 0: "OFF", 1: "ON", 2: "DAYTIME_RUNNING", 256: "AUTO" } as const;
const WIPER_STATE_ENUMS = { 0: "OTHER", 1: "OFF", 2: "ON", 3: "SERVICE" } as const;
const WIPER_SWITCH_ENUMS = { 0: "OTHER", 1: "OFF", 2: "MIST", 3: "INTERMITTENT_LEVEL_1", 4: "INTERMITTENT_LEVEL_2", 5: "INTERMITTENT_LEVEL_3", 6: "INTERMITTENT_LEVEL_4", 7: "INTERMITTENT_LEVEL_5", 8: "CONTINUOUS_LEVEL_1", 9: "CONTINUOUS_LEVEL_2", 10: "CONTINUOUS_LEVEL_3", 11: "CONTINUOUS_LEVEL_4", 12: "CONTINUOUS_LEVEL_5", 13: "AUTO", 14: "SERVICE" } as const;

/**
 * Property database with human-readable information.
 */
export const VHAL_PROPERTY_DATABASE: Record<number, Partial<VhalPropertyInfo>> = {
  // ── Info ────────────────────────────────────────────────────────
  [VhalPropertyId.INFO_VIN]: { name: "VIN", type: VhalPropertyType.STRING, description: "Vehicle Identification Number", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_MAKE]: { name: "Make", type: VhalPropertyType.STRING, description: "Vehicle manufacturer", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_MODEL]: { name: "Model", type: VhalPropertyType.STRING, description: "Vehicle model", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_MODEL_YEAR]: { name: "Model Year", type: VhalPropertyType.INT32, description: "Vehicle model year", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_FUEL_CAPACITY]: { name: "Fuel Capacity", type: VhalPropertyType.FLOAT, description: "Fuel tank capacity", units: "mL", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_FUEL_TYPE]: { name: "Fuel Type", type: VhalPropertyType.INT32_VEC, description: "Fuel types supported", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_EV_BATTERY_CAPACITY]: { name: "EV Battery Capacity", type: VhalPropertyType.FLOAT, description: "EV battery nominal capacity", units: "Wh", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_EV_CONNECTOR_TYPE]: { name: "EV Connector Type", type: VhalPropertyType.INT32_VEC, description: "EV connector types", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_FUEL_DOOR_LOCATION]: { name: "Fuel Door Location", type: VhalPropertyType.INT32, description: "Fuel door location", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_EV_PORT_LOCATION]: { name: "EV Port Location", type: VhalPropertyType.INT32, description: "EV charge port location", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_DRIVER_SEAT]: { name: "Driver Seat", type: VhalPropertyType.INT32, description: "Driver seat location", category: "Info", readOnly: true },
  [VhalPropertyId.INFO_EXTERIOR_DIMENSIONS]: { name: "Exterior Dimensions", type: VhalPropertyType.INT32_VEC, description: "Vehicle exterior dimensions", category: "Info", readOnly: true },
  [VhalPropertyId.VEHICLE_CURB_WEIGHT]: { name: "Curb Weight", type: VhalPropertyType.INT32, description: "Vehicle curb weight", units: "kg", category: "Info", readOnly: true },

  // ── Performance ────────────────────────────────────────────────
  [VhalPropertyId.PERF_VEHICLE_SPEED]: { name: "Vehicle Speed", type: VhalPropertyType.FLOAT, description: "Current vehicle speed", units: "m/s", category: "Performance" },
  [VhalPropertyId.PERF_VEHICLE_SPEED_DISPLAY]: { name: "Display Speed", type: VhalPropertyType.FLOAT, description: "Display vehicle speed", units: "m/s", category: "Performance", readOnly: true },
  [VhalPropertyId.PERF_ODOMETER]: { name: "Odometer", type: VhalPropertyType.FLOAT, description: "Total distance traveled", units: "km", category: "Performance", readOnly: true },
  [VhalPropertyId.PERF_STEERING_ANGLE]: { name: "Steering Angle", type: VhalPropertyType.FLOAT, description: "Steering wheel angle", units: "deg", category: "Performance" },
  [VhalPropertyId.PERF_REAR_STEERING_ANGLE]: { name: "Rear Steering Angle", type: VhalPropertyType.FLOAT, description: "Rear axle steering angle", units: "deg", category: "Performance", readOnly: true },

  // ── Fuel ───────────────────────────────────────────────────────
  [VhalPropertyId.FUEL_LEVEL]: { name: "Fuel Level", type: VhalPropertyType.FLOAT, description: "Fuel level", units: "mL", category: "Fuel" },
  [VhalPropertyId.FUEL_DOOR_OPEN]: { name: "Fuel Door Open", type: VhalPropertyType.BOOLEAN, description: "Whether the fuel door is open", category: "Fuel" },
  [VhalPropertyId.FUEL_LEVEL_LOW]: { name: "Fuel Level Low", type: VhalPropertyType.BOOLEAN, description: "Low fuel warning", category: "Fuel", readOnly: true },
  [VhalPropertyId.RANGE_REMAINING]: { name: "Range Remaining", type: VhalPropertyType.FLOAT, description: "Estimated driving range remaining", units: "m", category: "Fuel" },

  // ── Engine ─────────────────────────────────────────────────────
  [VhalPropertyId.ENGINE_RPM]: { name: "Engine RPM", type: VhalPropertyType.FLOAT, description: "Engine revolutions per minute", units: "RPM", category: "Engine" },
  [VhalPropertyId.ENGINE_OIL_LEVEL]: { name: "Oil Level", type: VhalPropertyType.INT32, description: "Engine oil level", category: "Engine", readOnly: true, enumValues: { 0: "CRITICALLY_LOW", 1: "LOW", 2: "NORMAL", 3: "HIGH", 4: "ERROR" } },
  [VhalPropertyId.ENGINE_COOLANT_TEMP]: { name: "Coolant Temp", type: VhalPropertyType.FLOAT, description: "Engine coolant temperature", units: "°C", category: "Engine" },
  [VhalPropertyId.ENGINE_OIL_TEMP]: { name: "Oil Temp", type: VhalPropertyType.FLOAT, description: "Engine oil temperature", units: "°C", category: "Engine" },
  [VhalPropertyId.ENGINE_IDLE_AUTO_STOP_ENABLED]: { name: "Idle Auto Stop", type: VhalPropertyType.BOOLEAN, description: "Engine idle auto stop enabled", category: "Engine" },

  // ── EV / Battery ───────────────────────────────────────────────
  [VhalPropertyId.EV_BATTERY_LEVEL]: { name: "EV Battery Level", type: VhalPropertyType.FLOAT, description: "EV battery energy level", units: "Wh", category: "Battery" },
  [VhalPropertyId.EV_CHARGE_PORT_OPEN]: { name: "Charge Port Open", type: VhalPropertyType.BOOLEAN, description: "Charge port open", category: "Battery" },
  [VhalPropertyId.EV_CHARGE_PORT_CONNECTED]: { name: "Charge Port Connected", type: VhalPropertyType.BOOLEAN, description: "Charger connected", category: "Battery", readOnly: true },
  [VhalPropertyId.EV_BATTERY_INSTANTANEOUS_CHARGE_RATE]: { name: "Charge Rate", type: VhalPropertyType.FLOAT, description: "Instantaneous battery charge rate", units: "mW", category: "Battery", readOnly: true },
  [VhalPropertyId.EV_CURRENT_BATTERY_CAPACITY]: { name: "Current Capacity", type: VhalPropertyType.FLOAT, description: "Current battery capacity", units: "Wh", category: "Battery", readOnly: true },
  [VhalPropertyId.EV_BATTERY_AVERAGE_TEMPERATURE]: { name: "Battery Temp", type: VhalPropertyType.FLOAT, description: "Battery average temperature", units: "°C", category: "Battery", readOnly: true },
  [VhalPropertyId.EV_CHARGE_CURRENT_DRAW_LIMIT]: { name: "Charge Current Limit", type: VhalPropertyType.FLOAT, description: "Max charge current draw", units: "A", category: "Battery" },
  [VhalPropertyId.EV_CHARGE_PERCENT_LIMIT]: { name: "Charge % Limit", type: VhalPropertyType.FLOAT, description: "Charge percent limit", units: "%", category: "Battery" },
  [VhalPropertyId.EV_CHARGE_STATE]: { name: "Charge State", type: VhalPropertyType.INT32, description: "EV charging state", category: "Battery", readOnly: true, enumValues: { 0: "UNKNOWN", 1: "CHARGING", 2: "FULLY_CHARGED", 3: "NOT_CHARGING", 4: "ERROR" } },
  [VhalPropertyId.EV_CHARGE_SWITCH]: { name: "Charge Switch", type: VhalPropertyType.BOOLEAN, description: "EV charge enable/disable", category: "Battery" },
  [VhalPropertyId.EV_CHARGE_TIME_REMAINING]: { name: "Charge Time Remaining", type: VhalPropertyType.INT32, description: "Estimated time to full charge", units: "s", category: "Battery", readOnly: true },
  [VhalPropertyId.EV_REGENERATIVE_BRAKING_STATE]: { name: "Regen Braking State", type: VhalPropertyType.INT32, description: "Regenerative braking state", category: "Battery", readOnly: true },
  [VhalPropertyId.EV_BRAKE_REGENERATION_LEVEL]: { name: "Regen Level", type: VhalPropertyType.INT32, description: "Brake regeneration level", category: "Battery", minValue: 0, maxValue: 3 },
  [VhalPropertyId.EV_STOPPING_MODE]: { name: "Stopping Mode", type: VhalPropertyType.INT32, description: "EV stopping mode", category: "Battery", enumValues: { 0: "OTHER", 1: "CREEP", 2: "ROLL", 3: "HOLD" } },

  // ── Transmission ───────────────────────────────────────────────
  [VhalPropertyId.GEAR_SELECTION]: { name: "Gear Selection", type: VhalPropertyType.INT32, description: "Driver-selected gear", category: "Transmission", enumValues: { 0: "UNKNOWN", 1: "NEUTRAL", 2: "REVERSE", 4: "PARK", 8: "DRIVE", 16: "SPORT", 32: "LOW", 64: "FIRST", 128: "SECOND", 256: "THIRD" } },
  [VhalPropertyId.CURRENT_GEAR]: { name: "Current Gear", type: VhalPropertyType.INT32, description: "Actual transmission gear", category: "Transmission", readOnly: true, enumValues: { 0: "UNKNOWN", 1: "NEUTRAL", 2: "REVERSE", 4: "PARK", 8: "DRIVE", 16: "FIRST", 32: "SECOND", 64: "THIRD", 128: "FOURTH", 256: "FIFTH" } },
  [VhalPropertyId.PARKING_BRAKE_ON]: { name: "Parking Brake", type: VhalPropertyType.BOOLEAN, description: "Parking brake engaged", category: "Transmission" },
  [VhalPropertyId.PARKING_BRAKE_AUTO_APPLY]: { name: "Auto Parking Brake", type: VhalPropertyType.BOOLEAN, description: "Automatic parking brake", category: "Transmission", readOnly: true },
  [VhalPropertyId.IGNITION_STATE]: { name: "Ignition State", type: VhalPropertyType.INT32, description: "Vehicle ignition state", category: "Transmission", enumValues: { 0: "UNDEFINED", 1: "LOCK", 2: "OFF", 3: "ACC", 4: "ON", 5: "START" } },
  [VhalPropertyId.TURN_SIGNAL_STATE]: { name: "Turn Signal", type: VhalPropertyType.INT32, description: "Turn signal state", category: "Transmission", readOnly: true, enumValues: { 0: "NONE", 1: "RIGHT", 2: "LEFT" } },
  [VhalPropertyId.ABS_ACTIVE]: { name: "ABS Active", type: VhalPropertyType.BOOLEAN, description: "Anti-lock braking active", category: "Safety", readOnly: true },
  [VhalPropertyId.TRACTION_CONTROL_ACTIVE]: { name: "Traction Control", type: VhalPropertyType.BOOLEAN, description: "Traction control active", category: "Safety", readOnly: true },
  [VhalPropertyId.ELECTRONIC_STABILITY_CONTROL_ENABLED]: { name: "ESC Enabled", type: VhalPropertyType.BOOLEAN, description: "Electronic stability control enabled", category: "Safety" },
  [VhalPropertyId.ELECTRONIC_STABILITY_CONTROL_STATE]: { name: "ESC State", type: VhalPropertyType.INT32, description: "Electronic stability control state", category: "Safety", readOnly: true },

  // ── HVAC ───────────────────────────────────────────────────────
  [VhalPropertyId.HVAC_POWER_ON]: { name: "HVAC Power", type: VhalPropertyType.BOOLEAN, description: "HVAC system power", category: "HVAC" },
  [VhalPropertyId.HVAC_AC_ON]: { name: "A/C", type: VhalPropertyType.BOOLEAN, description: "Air conditioning", category: "HVAC" },
  [VhalPropertyId.HVAC_MAX_AC_ON]: { name: "Max A/C", type: VhalPropertyType.BOOLEAN, description: "Maximum A/C mode", category: "HVAC" },
  [VhalPropertyId.HVAC_MAX_DEFROST_ON]: { name: "Max Defrost", type: VhalPropertyType.BOOLEAN, description: "Maximum defrost", category: "HVAC" },
  [VhalPropertyId.HVAC_RECIRC_ON]: { name: "Recirculation", type: VhalPropertyType.BOOLEAN, description: "Air recirculation", category: "HVAC" },
  [VhalPropertyId.HVAC_AUTO_RECIRC_ON]: { name: "Auto Recirc", type: VhalPropertyType.BOOLEAN, description: "Auto recirculation", category: "HVAC" },
  [VhalPropertyId.HVAC_DUAL_ON]: { name: "Dual Zone", type: VhalPropertyType.BOOLEAN, description: "Dual-zone climate", category: "HVAC" },
  [VhalPropertyId.HVAC_AUTO_ON]: { name: "Auto Climate", type: VhalPropertyType.BOOLEAN, description: "Automatic climate control", category: "HVAC" },
  [VhalPropertyId.HVAC_FAN_SPEED]: { name: "Fan Speed", type: VhalPropertyType.INT32, description: "HVAC fan speed level", category: "HVAC", minValue: 1, maxValue: 7 },
  [VhalPropertyId.HVAC_FAN_DIRECTION]: { name: "Fan Direction", type: VhalPropertyType.INT32, description: "Airflow direction", category: "HVAC", enumValues: { 1: "FACE", 2: "FLOOR", 3: "FACE_AND_FLOOR", 4: "DEFROST", 5: "DEFROST_AND_FLOOR" } },
  [VhalPropertyId.HVAC_TEMPERATURE_SET]: { name: "Temp Set", type: VhalPropertyType.FLOAT, description: "HVAC temperature setpoint", units: "°C", category: "HVAC", minValue: 16, maxValue: 28 },
  [VhalPropertyId.HVAC_TEMPERATURE_CURRENT]: { name: "Cabin Temp", type: VhalPropertyType.FLOAT, description: "Current cabin temperature", units: "°C", category: "HVAC", readOnly: true },
  [VhalPropertyId.HVAC_DEFROSTER]: { name: "Defroster", type: VhalPropertyType.BOOLEAN, description: "Windshield defroster", category: "HVAC" },
  [VhalPropertyId.HVAC_ELECTRIC_DEFROSTER_ON]: { name: "Electric Defroster", type: VhalPropertyType.BOOLEAN, description: "Electric defroster", category: "HVAC" },
  [VhalPropertyId.HVAC_SEAT_TEMPERATURE]: { name: "Seat Temp", type: VhalPropertyType.INT32, description: "Seat heating/cooling level", category: "HVAC", minValue: -2, maxValue: 2 },
  [VhalPropertyId.HVAC_SEAT_VENTILATION]: { name: "Seat Ventilation", type: VhalPropertyType.INT32, description: "Seat ventilation level", category: "HVAC", minValue: 0, maxValue: 3 },
  [VhalPropertyId.HVAC_STEERING_WHEEL_HEAT]: { name: "Wheel Heat", type: VhalPropertyType.INT32, description: "Steering wheel heater level", category: "HVAC", minValue: -2, maxValue: 2 },
  [VhalPropertyId.HVAC_SIDE_MIRROR_HEAT]: { name: "Mirror Heat", type: VhalPropertyType.INT32, description: "Side mirror heater level", category: "HVAC", minValue: 0, maxValue: 2 },
  [VhalPropertyId.HVAC_TEMPERATURE_DISPLAY_UNITS]: { name: "Temp Units", type: VhalPropertyType.INT32, description: "Temperature display units", category: "Display", enumValues: { 48: "CELSIUS", 49: "FAHRENHEIT" } },
  [VhalPropertyId.HVAC_ACTUAL_FAN_SPEED_RPM]: { name: "Actual Fan RPM", type: VhalPropertyType.INT32, description: "Actual fan speed", units: "RPM", category: "HVAC", readOnly: true },

  // ── Display Units ──────────────────────────────────────────────
  [VhalPropertyId.DISTANCE_DISPLAY_UNITS]: { name: "Distance Units", type: VhalPropertyType.INT32, description: "Distance display units", category: "Display", enumValues: { 33: "METERS", 35: "KILOMETERS", 36: "MILES" } },
  [VhalPropertyId.FUEL_VOLUME_DISPLAY_UNITS]: { name: "Fuel Volume Units", type: VhalPropertyType.INT32, description: "Fuel volume display units", category: "Display", enumValues: { 65: "LITERS", 66: "US_GALLONS", 67: "IMPERIAL_GALLONS" } },
  [VhalPropertyId.VEHICLE_SPEED_DISPLAY_UNITS]: { name: "Speed Units", type: VhalPropertyType.INT32, description: "Vehicle speed display units", category: "Display", enumValues: { 1: "METERS_PER_SEC", 144: "MPH", 147: "KPH" } },
  [VhalPropertyId.TIRE_PRESSURE_DISPLAY_UNITS]: { name: "Tire Pressure Units", type: VhalPropertyType.INT32, description: "Tire pressure display units", category: "Display", enumValues: { 112: "KILOPASCAL", 113: "PSI", 114: "BAR" } },
  [VhalPropertyId.EV_BATTERY_DISPLAY_UNITS]: { name: "Battery Units", type: VhalPropertyType.INT32, description: "EV battery display units", category: "Display", enumValues: { 96: "WH", 100: "AH", 101: "KWH" } },
  [VhalPropertyId.DISPLAY_BRIGHTNESS]: { name: "Brightness", type: VhalPropertyType.INT32, description: "Display brightness", category: "Display", minValue: 0, maxValue: 100 },

  // ── Environment ────────────────────────────────────────────────
  [VhalPropertyId.ENV_OUTSIDE_TEMPERATURE]: { name: "Outside Temp", type: VhalPropertyType.FLOAT, description: "Outside ambient temperature", units: "°C", category: "Environment", readOnly: true },
  [VhalPropertyId.NIGHT_MODE]: { name: "Night Mode", type: VhalPropertyType.BOOLEAN, description: "Night mode active", category: "Environment", readOnly: true },

  // ── Lights ─────────────────────────────────────────────────────
  [VhalPropertyId.HEADLIGHTS_STATE]: { name: "Headlights State", type: VhalPropertyType.INT32, description: "Current headlight state", category: "Lights", readOnly: true, enumValues: { ...LIGHT_ENUMS } },
  [VhalPropertyId.HEADLIGHTS_SWITCH]: { name: "Headlights Switch", type: VhalPropertyType.INT32, description: "Headlight switch", category: "Lights", enumValues: { ...LIGHT_SWITCH_ENUMS } },
  [VhalPropertyId.HIGH_BEAM_LIGHTS_STATE]: { name: "High Beams State", type: VhalPropertyType.INT32, description: "High beam state", category: "Lights", readOnly: true, enumValues: { 0: "OFF", 1: "ON" } },
  [VhalPropertyId.HIGH_BEAM_LIGHTS_SWITCH]: { name: "High Beams Switch", type: VhalPropertyType.INT32, description: "High beam switch", category: "Lights", enumValues: { 0: "OFF", 1: "ON", 256: "AUTO" } },
  [VhalPropertyId.HAZARD_LIGHTS_STATE]: { name: "Hazard State", type: VhalPropertyType.INT32, description: "Hazard light state", category: "Lights", readOnly: true, enumValues: { 0: "OFF", 1: "ON" } },
  [VhalPropertyId.HAZARD_LIGHTS_SWITCH]: { name: "Hazard Switch", type: VhalPropertyType.INT32, description: "Hazard light switch", category: "Lights", enumValues: { 0: "OFF", 1: "ON" } },
  [VhalPropertyId.FOG_LIGHTS_STATE]: { name: "Fog Lights State", type: VhalPropertyType.INT32, description: "Fog light state (front and rear together)", category: "Lights", readOnly: true, enumValues: { 0: "OFF", 1: "ON" } },
  [VhalPropertyId.FOG_LIGHTS_SWITCH]: { name: "Fog Lights Switch", type: VhalPropertyType.INT32, description: "Fog light switch (front and rear together)", category: "Lights", enumValues: { 0: "OFF", 1: "ON", 256: "AUTO" } },
  [VhalPropertyId.FRONT_FOG_LIGHTS_STATE]: { name: "Front Fog State", type: VhalPropertyType.INT32, description: "Front fog light state", category: "Lights", readOnly: true, enumValues: { 0: "OFF", 1: "ON" } },
  [VhalPropertyId.FRONT_FOG_LIGHTS_SWITCH]: { name: "Front Fog Switch", type: VhalPropertyType.INT32, description: "Front fog light switch", category: "Lights", enumValues: { 0: "OFF", 1: "ON" } },
  [VhalPropertyId.REAR_FOG_LIGHTS_STATE]: { name: "Rear Fog State", type: VhalPropertyType.INT32, description: "Rear fog light state", category: "Lights", readOnly: true, enumValues: { 0: "OFF", 1: "ON" } },
  [VhalPropertyId.REAR_FOG_LIGHTS_SWITCH]: { name: "Rear Fog Switch", type: VhalPropertyType.INT32, description: "Rear fog light switch", category: "Lights", enumValues: { 0: "OFF", 1: "ON" } },
  [VhalPropertyId.CABIN_LIGHTS_STATE]: { name: "Cabin Lights State", type: VhalPropertyType.INT32, description: "Cabin lights state", category: "Lights", readOnly: true, enumValues: { 0: "OFF", 1: "ON" } },
  [VhalPropertyId.CABIN_LIGHTS_SWITCH]: { name: "Cabin Lights", type: VhalPropertyType.INT32, description: "Cabin lights switch", category: "Lights", enumValues: { 0: "OFF", 1: "ON", 256: "AUTO" } },
  [VhalPropertyId.STEERING_WHEEL_LIGHTS_STATE]: { name: "Wheel Lights State", type: VhalPropertyType.INT32, description: "Steering wheel lights state", category: "Lights", readOnly: true, enumValues: { 0: "OFF", 1: "ON" } },
  [VhalPropertyId.STEERING_WHEEL_LIGHTS_SWITCH]: { name: "Wheel Lights", type: VhalPropertyType.INT32, description: "Steering wheel lights switch", category: "Lights", enumValues: { 0: "OFF", 1: "ON", 256: "AUTO" } },

  // ── Doors ──────────────────────────────────────────────────────
  [VhalPropertyId.DOOR_POS]: { name: "Door Position", type: VhalPropertyType.INT32, description: "Door position", category: "Doors" },
  [VhalPropertyId.DOOR_MOVE]: { name: "Door Move", type: VhalPropertyType.INT32, description: "Door movement direction", category: "Doors" },
  [VhalPropertyId.DOOR_LOCK]: { name: "Door Lock", type: VhalPropertyType.BOOLEAN, description: "Door lock status", category: "Doors" },
  [VhalPropertyId.DOOR_CHILD_LOCK_ENABLED]: { name: "Child Lock", type: VhalPropertyType.BOOLEAN, description: "Child lock enabled", category: "Doors" },

  // ── Windows ────────────────────────────────────────────────────
  [VhalPropertyId.WINDOW_POS]: { name: "Window Position", type: VhalPropertyType.INT32, description: "Window position", category: "Windows" },
  [VhalPropertyId.WINDOW_MOVE]: { name: "Window Move", type: VhalPropertyType.INT32, description: "Window movement", category: "Windows" },
  [VhalPropertyId.WINDOW_LOCK]: { name: "Window Lock", type: VhalPropertyType.BOOLEAN, description: "Child window lock", category: "Windows" },
  [VhalPropertyId.WINDSHIELD_WIPERS_STATE]: { name: "Wiper State", type: VhalPropertyType.INT32, description: "Wiper state", category: "Wipers", readOnly: true, enumValues: { ...WIPER_STATE_ENUMS } },
  [VhalPropertyId.WINDSHIELD_WIPERS_SWITCH]: { name: "Wiper Switch", type: VhalPropertyType.INT32, description: "Wiper switch", category: "Wipers", enumValues: { ...WIPER_SWITCH_ENUMS } },
  [VhalPropertyId.WINDSHIELD_WIPERS_PERIOD]: { name: "Wiper Period", type: VhalPropertyType.INT32, description: "Wiper period", units: "ms", category: "Wipers", readOnly: true },

  // ── Seats ──────────────────────────────────────────────────────
  [VhalPropertyId.SEAT_BELT_BUCKLED]: { name: "Seat Belt", type: VhalPropertyType.BOOLEAN, description: "Seat belt buckled", category: "Seats" },
  [VhalPropertyId.SEAT_OCCUPANCY]: { name: "Seat Occupancy", type: VhalPropertyType.INT32, description: "Seat occupancy state", category: "Seats", readOnly: true, enumValues: { 0: "UNKNOWN", 1: "VACANT", 2: "OCCUPIED" } },
  [VhalPropertyId.SEAT_FORE_AFT_POS]: { name: "Seat Fore/Aft", type: VhalPropertyType.INT32, description: "Seat fore/aft position", category: "Seats", minValue: -10, maxValue: 10 },
  [VhalPropertyId.SEAT_BACKREST_ANGLE_1_POS]: { name: "Backrest Angle", type: VhalPropertyType.INT32, description: "Seat backrest angle", category: "Seats", minValue: -10, maxValue: 10 },
  [VhalPropertyId.SEAT_HEIGHT_POS]: { name: "Seat Height", type: VhalPropertyType.INT32, description: "Seat height position", category: "Seats", minValue: -10, maxValue: 10 },
  [VhalPropertyId.SEAT_HEADREST_HEIGHT_POS_V2]: { name: "Headrest Height", type: VhalPropertyType.INT32, description: "Headrest height", category: "Seats", minValue: 0, maxValue: 10 },
  [VhalPropertyId.SEAT_LUMBAR_FORE_AFT_POS]: { name: "Lumbar Support", type: VhalPropertyType.INT32, description: "Lumbar support fore/aft", category: "Seats", minValue: -10, maxValue: 10 },
  [VhalPropertyId.SEAT_TILT_POS]: { name: "Seat Tilt", type: VhalPropertyType.INT32, description: "Seat tilt position", category: "Seats", minValue: -10, maxValue: 10 },
  [VhalPropertyId.SEAT_EASY_ACCESS_ENABLED]: { name: "Easy Access", type: VhalPropertyType.BOOLEAN, description: "Seat easy access enabled", category: "Seats" },
  [VhalPropertyId.SEAT_AIRBAG_ENABLED]: { name: "Airbag Enabled", type: VhalPropertyType.BOOLEAN, description: "Seat airbag enabled", category: "Seats" },

  // ── Mirrors ────────────────────────────────────────────────────
  [VhalPropertyId.MIRROR_Z_POS]: { name: "Mirror Z Pos", type: VhalPropertyType.INT32, description: "Mirror vertical position", category: "Mirrors", minValue: -3, maxValue: 3 },
  [VhalPropertyId.MIRROR_Y_POS]: { name: "Mirror Y Pos", type: VhalPropertyType.INT32, description: "Mirror horizontal position", category: "Mirrors", minValue: -3, maxValue: 3 },
  [VhalPropertyId.MIRROR_LOCK]: { name: "Mirror Lock", type: VhalPropertyType.BOOLEAN, description: "Mirror lock", category: "Mirrors" },
  [VhalPropertyId.MIRROR_FOLD]: { name: "Mirror Fold", type: VhalPropertyType.BOOLEAN, description: "Mirror fold state", category: "Mirrors" },
  [VhalPropertyId.MIRROR_AUTO_FOLD_ENABLED]: { name: "Auto Fold", type: VhalPropertyType.BOOLEAN, description: "Mirror auto fold", category: "Mirrors" },
  [VhalPropertyId.MIRROR_AUTO_TILT_ENABLED]: { name: "Auto Tilt", type: VhalPropertyType.BOOLEAN, description: "Mirror auto tilt", category: "Mirrors" },

  // ── Steering Wheel ─────────────────────────────────────────────
  [VhalPropertyId.STEERING_WHEEL_DEPTH_POS]: { name: "Depth Position", type: VhalPropertyType.INT32, description: "Steering wheel depth", category: "Steering", minValue: 0, maxValue: 10 },
  [VhalPropertyId.STEERING_WHEEL_HEIGHT_POS]: { name: "Height Position", type: VhalPropertyType.INT32, description: "Steering wheel height", category: "Steering", minValue: 0, maxValue: 10 },
  [VhalPropertyId.STEERING_WHEEL_THEFT_LOCK_ENABLED]: { name: "Theft Lock", type: VhalPropertyType.BOOLEAN, description: "Steering wheel theft lock", category: "Steering" },
  [VhalPropertyId.STEERING_WHEEL_LOCKED]: { name: "Locked", type: VhalPropertyType.BOOLEAN, description: "Steering wheel locked", category: "Steering" },
  [VhalPropertyId.STEERING_WHEEL_EASY_ACCESS_ENABLED]: { name: "Easy Access", type: VhalPropertyType.BOOLEAN, description: "Steering wheel easy access", category: "Steering" },

  // ── Tires ──────────────────────────────────────────────────────
  [VhalPropertyId.TIRE_PRESSURE]: { name: "Tire Pressure", type: VhalPropertyType.FLOAT, description: "Tire pressure", units: "kPa", category: "Tires", readOnly: true },
  [VhalPropertyId.CRITICALLY_LOW_TIRE_PRESSURE]: { name: "Critical Low Pressure", type: VhalPropertyType.FLOAT, description: "Critically low tire pressure threshold", units: "kPa", category: "Tires", readOnly: true },

  // ── Cruise Control ─────────────────────────────────────────────
  [VhalPropertyId.CRUISE_CONTROL_ENABLED]: { name: "Cruise Control", type: VhalPropertyType.BOOLEAN, description: "Cruise control enabled", category: "Cruise Control" },
  [VhalPropertyId.CRUISE_CONTROL_TYPE]: { name: "Cruise Type", type: VhalPropertyType.INT32, description: "Cruise control type", category: "Cruise Control", enumValues: { 0: "OTHER", 1: "STANDARD", 2: "ADAPTIVE" } },
  [VhalPropertyId.CRUISE_CONTROL_STATE]: { name: "Cruise State", type: VhalPropertyType.INT32, description: "Cruise control state", category: "Cruise Control", readOnly: true, enumValues: { 0: "OTHER", 1: "ENABLED", 2: "ACTIVATED", 3: "USER_OVERRIDE", 4: "SUSPENDED", 5: "FORCED_DEACTIVATED" } },
  [VhalPropertyId.CRUISE_CONTROL_TARGET_SPEED]: { name: "Cruise Speed", type: VhalPropertyType.FLOAT, description: "Cruise control target speed", units: "m/s", category: "Cruise Control", readOnly: true },

  // ── ADAS ───────────────────────────────────────────────────────
  [VhalPropertyId.AUTOMATIC_EMERGENCY_BRAKING_ENABLED]: { name: "Auto Emergency Braking", type: VhalPropertyType.BOOLEAN, description: "Automatic emergency braking enabled", category: "ADAS" },
  [VhalPropertyId.FORWARD_COLLISION_WARNING_ENABLED]: { name: "Forward Collision Warning", type: VhalPropertyType.BOOLEAN, description: "Forward collision warning enabled", category: "ADAS" },
  [VhalPropertyId.BLIND_SPOT_WARNING_ENABLED]: { name: "Blind Spot Warning", type: VhalPropertyType.BOOLEAN, description: "Blind spot warning enabled", category: "ADAS" },
  [VhalPropertyId.LANE_DEPARTURE_WARNING_ENABLED]: { name: "Lane Departure Warning", type: VhalPropertyType.BOOLEAN, description: "Lane departure warning enabled", category: "ADAS" },
  [VhalPropertyId.LANE_KEEP_ASSIST_ENABLED]: { name: "Lane Keep Assist", type: VhalPropertyType.BOOLEAN, description: "Lane keep assist enabled", category: "ADAS" },
  [VhalPropertyId.LANE_CENTERING_ASSIST_ENABLED]: { name: "Lane Centering", type: VhalPropertyType.BOOLEAN, description: "Lane centering assist enabled", category: "ADAS" },
  [VhalPropertyId.EMERGENCY_LANE_KEEP_ASSIST_ENABLED]: { name: "Emergency Lane Keep", type: VhalPropertyType.BOOLEAN, description: "Emergency lane keep assist enabled", category: "ADAS" },
  [VhalPropertyId.HANDS_ON_DETECTION_ENABLED]: { name: "Hands-On Detection", type: VhalPropertyType.BOOLEAN, description: "Hands-on detection enabled", category: "ADAS" },
  [VhalPropertyId.DRIVER_DROWSINESS_ATTENTION_SYSTEM_ENABLED]: { name: "Drowsiness Detection", type: VhalPropertyType.BOOLEAN, description: "Driver drowsiness/attention system", category: "ADAS" },
  [VhalPropertyId.DRIVER_DROWSINESS_ATTENTION_WARNING_ENABLED]: { name: "Drowsiness Warning", type: VhalPropertyType.BOOLEAN, description: "Driver drowsiness warning", category: "ADAS" },
  [VhalPropertyId.DRIVER_DISTRACTION_SYSTEM_ENABLED]: { name: "Distraction Detection", type: VhalPropertyType.BOOLEAN, description: "Driver distraction system", category: "ADAS" },
  [VhalPropertyId.DRIVER_DISTRACTION_WARNING_ENABLED]: { name: "Distraction Warning", type: VhalPropertyType.BOOLEAN, description: "Driver distraction warning", category: "ADAS" },
  [VhalPropertyId.LOW_SPEED_COLLISION_WARNING_ENABLED]: { name: "Low Speed Collision", type: VhalPropertyType.BOOLEAN, description: "Low speed collision warning", category: "ADAS" },
  [VhalPropertyId.CROSS_TRAFFIC_MONITORING_ENABLED]: { name: "Cross Traffic Monitor", type: VhalPropertyType.BOOLEAN, description: "Cross traffic monitoring", category: "ADAS" },
  [VhalPropertyId.LOW_SPEED_AUTOMATIC_EMERGENCY_BRAKING_ENABLED]: { name: "Low Speed AEB", type: VhalPropertyType.BOOLEAN, description: "Low speed automatic emergency braking", category: "ADAS" },

  // ── Misc ───────────────────────────────────────────────────────
  [VhalPropertyId.VALET_MODE_ENABLED]: { name: "Valet Mode", type: VhalPropertyType.BOOLEAN, description: "Valet mode enabled", category: "Safety" },
  [VhalPropertyId.VEHICLE_IN_USE]: { name: "Vehicle In Use", type: VhalPropertyType.BOOLEAN, description: "Vehicle currently in use", category: "Info" },
  [VhalPropertyId.TRAILER_PRESENT]: { name: "Trailer Present", type: VhalPropertyType.INT32, description: "Trailer presence status", category: "Info", readOnly: true, enumValues: { 0: "UNKNOWN", 1: "NOT_PRESENT", 2: "PRESENT", 3: "ERROR" } },
};

function toTitleCase(name: string): string {
  return name
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getPropertyInfo(
  propertyId: number,
  deviceName?: string,
): VhalPropertyInfo {
  const dbInfo = VHAL_PROPERTY_DATABASE[propertyId];
  const type = getPropertyType(propertyId);

  const deviceLabel =
    !deviceName ? null
    : deviceName === "VENDOR_PROPERTY"
      ? `Vendor Property (0x${propertyId.toString(16).toUpperCase()})`
      : toTitleCase(deviceName);

  const name = dbInfo?.name || deviceLabel || `Property 0x${propertyId.toString(16).toUpperCase()}`;
  const category = dbInfo?.category || deriveCategoryFromName(deviceName) || "Unknown";

  return {
    id: propertyId,
    name,
    type: dbInfo?.type || type,
    description: dbInfo?.description || (deviceName ? `${deviceName} (${category})` : "Unknown property"),
    units: dbInfo?.units,
    category,
    readOnly: dbInfo?.readOnly,
    minValue: dbInfo?.minValue,
    maxValue: dbInfo?.maxValue,
    enumValues: dbInfo?.enumValues,
  };
}

function deriveCategoryFromName(name?: string): string | null {
  if (!name) return null;
  const n = name.toUpperCase();
  if (n.startsWith("HVAC_")) return "HVAC";
  if (n.startsWith("SEAT_")) return "Seats";
  if (n.startsWith("DOOR_")) return "Doors";
  if (n.startsWith("WINDOW_")) return "Windows";
  if (n.startsWith("MIRROR_")) return "Mirrors";
  if (n.startsWith("TIRE_") || n.startsWith("WHEEL_")) return "Tires";
  if (n.startsWith("ENGINE_")) return "Engine";
  if (n.startsWith("EV_") || n.startsWith("BATTERY_")) return "Battery";
  if (n.startsWith("INFO_")) return "Info";
  if (n.startsWith("PERF_")) return "Performance";
  if (n.startsWith("FUEL_")) return "Fuel";
  if (n.startsWith("HEADLIGHT") || n.startsWith("HIGH_BEAM") || n.startsWith("FOG_") ||
      n.startsWith("HAZARD") || n.startsWith("CABIN_") || n.startsWith("READING_") ||
      n.startsWith("FRONT_FOG") || n.startsWith("REAR_FOG") || n.startsWith("STEERING_WHEEL_LIGHTS")) return "Lights";
  if (n.startsWith("CRUISE_") || n.startsWith("ADAPTIVE_CRUISE")) return "Cruise Control";
  if (n.startsWith("LANE_") || n.startsWith("BLIND_") || n.startsWith("FORWARD_") ||
      n.startsWith("LOW_SPEED_") || n.startsWith("CROSS_") || n.startsWith("AUTOMATIC_EMERGENCY") ||
      n.startsWith("DRIVER_") || n.startsWith("HANDS_") || n.startsWith("EMERGENCY_LANE")) return "ADAS";
  if (n.startsWith("STEERING_WHEEL")) return "Steering";
  if (n.startsWith("WINDSHIELD_WIPER")) return "Wipers";
  if (n.startsWith("GEAR_") || n.startsWith("CURRENT_GEAR") || n.startsWith("PARKING_") ||
      n.startsWith("IGNITION_") || n.startsWith("TURN_SIGNAL")) return "Transmission";
  if (n.includes("DISPLAY_UNITS") || n === "DISPLAY_BRIGHTNESS") return "Display";
  if (n.startsWith("VENDOR_") || n.startsWith("0x2")) return "Vendor";
  return "Vehicle";
}
